import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import DetailsPageHeader from "../../../../common/DetailsPageHeader";
import RichTextEditor from "../../../../common/RichTextEditor";
import Sidebar from "../../../../common/Sidebar";
import { mainLayoutStyles, routes, showNotification } from "../../../../constants";
import { useAuth } from "../../../../hooks";
import {
  useGetQCTemplateById,
  useSaveQCTemplate,
} from "../../hooks/QCTemplatesHooks";
import { QC_GREETING_TOKENS } from "../../hooks/qcGreeting";

/**
 * View / edit a QuickConnect (QuickCollect) guest-page template.
 *
 * When the org is inheriting the shared `default` template, saving creates a new
 * org-scoped document rather than mutating the shared default.
 *
 * See docs/QUICKCOLLECT_IMPLEMENTATION.md
 */
const QCTemplateDetailsContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { templateId } = useParams();
  const { currentUser, organizationId } = useAuth();

  const userId = currentUser?.id;

  const { template, loading: fetchLoading, refetch } =
    useGetQCTemplateById(templateId);
  const { saveTemplate, loading: saveLoading } = useSaveQCTemplate();

  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({ name: "", greeting: "" });

  // `isDefault` comes from the row click, with the fetched doc as the fallback
  // (covers a direct page load / refresh).
  const isDefault =
    template?.isDefault ?? location.state?.isDefault ?? false;

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || "",
        greeting: template.greeting || "",
      });
    }
  }, [template]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showNotification("Please enter a template name");
      return;
    }

    const result = await saveTemplate({
      templateId,
      isDefault,
      formData,
      userId,
      organizationId,
    });

    if (!result.success) {
      showNotification("Error saving template");
      return;
    }

    setIsEditMode(false);

    if (result.created) {
      // We just forked the shared default into an org-specific template.
      showNotification("QuickConnect template saved for your organization");
      navigate(routes.qcTemplateDetails.replace(":templateId", result.id), {
        replace: true,
        state: { isDefault: false },
      });
    } else {
      showNotification("Template updated successfully");
      refetch();
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    if (template) {
      setFormData({
        name: template.name || "",
        greeting: template.greeting || "",
      });
    }
  };

  const isSaving = saveLoading;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={formData.name || "QuickConnect Template"}
        bgColor="#fff"
        buttonText={isEditMode ? undefined : "Edit"}
        editEnabled={isEditMode}
        setEditEnabled={setIsEditMode}
        submitButtonText={"Save"}
        cancelButton={isEditMode}
        onCancelClick={handleCancel}
        handleSave={handleSave}
        loading={isSaving}
        onBackClick={() => navigate(routes.emailTemplates)}
      />
      <Sidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} style={{ padding: 30, marginTop: 40 }}>
          <Paper sx={{ p: 3, maxWidth: 900 }}>
            {isDefault ? (
              <Box
                sx={{
                  mb: 3,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: "#FFF8E1",
                  border: "1px solid #FFE082",
                }}
              >
                <Typography sx={{ ...labelStyles, color: "#8D6E00" }}>
                  Your organization is using the default QuickConnect template.
                  Saving changes will create your own copy.
                </Typography>
              </Box>
            ) : null}

            {/* Name */}
            <Typography sx={labelStyles}>Template Name</Typography>
            <TextField
              fullWidth
              size="small"
              value={formData.name}
              disabled={!isEditMode}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              sx={{ mb: 3, maxWidth: 500 }}
            />

            {/* Greeting */}
            <Typography sx={labelStyles}>Greeting</Typography>
            <Typography sx={helperStyles}>
              Shown at the top of the QuickConnect submission page. Available
              tokens: {QC_GREETING_TOKENS.join(", ")}
            </Typography>
            {/* Same component in both modes so view and edit render
                identically; disabled just hides the toolbar. */}
            <RichTextEditor
              value={formData.greeting}
              disabled={!isEditMode}
              onChange={(html) =>
                setFormData((prev) => ({ ...prev, greeting: html }))
              }
            />
          </Paper>
        </Grid>
      </Grid>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={fetchLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default QCTemplateDetailsContainer;

const labelStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#0A0A0AAB",
  mb: 1,
};

const helperStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 12,
  color: "#808080",
  mb: 1,
};
